import { useMemo } from "react";

import { Observation } from "@medplum/fhirtypes";

import { Group, List, Table } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import PostpartumTimeCell from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/PostpartumTable/PostpartumTimeCell";
import LastUpdated from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/LastUpdated";
import PreviousVersions from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions";
import classes from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions.module.css";
import { hasPreviousVersions } from "@/encounters/components/EncounterLabor/services/labor";
import { getPlacentaRowValues } from "@/encounters/components/EncounterLabor/utils/postpartum";

interface PlacentaRowProps {
  time: string;
  observation: Observation | undefined;
  showNumbersOnly?: boolean;
  showPreviousVersions?: boolean;
  onRefresh?: () => Promise<void>;
  isViewOnly: boolean;
}

interface PlacentaItem {
  label: string;
  value: string | undefined;
  showOther?: boolean;
  otherValue?: string | undefined;
}

export default function PlacentaRow({
  time,
  observation,
  showNumbersOnly,
  showPreviousVersions = true,
  onRefresh,
  isViewOnly,
}: PlacentaRowProps) {
  const {
    deliveryMethod,
    presentation,
    threeVesselCord,
    intact,
    appearance,
    notes,
    hasAnswers,
    hasNotes,
  } = useMemo(
    () =>
      observation
        ? getPlacentaRowValues(observation)
        : {
            deliveryMethod: undefined,
            presentation: undefined,
            threeVesselCord: undefined,
            intact: undefined,
            appearance: undefined,
            notes: undefined,
            hasAnswers: false,
            hasNotes: false,
          },
    [observation],
  );

  const placentaItems = useMemo<PlacentaItem[]>(
    () => [
      { label: "Delivery Method", value: deliveryMethod },
      { label: "Presentation", value: presentation },
      { label: "Three Vessel Cord", value: threeVesselCord },
      { label: "Intact", value: intact },
      { label: "Appearance", value: appearance },
    ],
    [deliveryMethod, presentation, threeVesselCord, intact, appearance],
  );

  if (!observation) return null;

  if ((!hasAnswers && !hasNotes) || showNumbersOnly) return null;

  const previousVersions = hasPreviousVersions(observation);

  return (
    <Table.Tr bg="white" className={classes.tableRow}>
      {showPreviousVersions && (
        <PostpartumTimeCell
          time={time}
          rowSpan={1}
          onRefresh={onRefresh}
          isViewOnly={isViewOnly}
          observation={observation}
        />
      )}
      <Table.Td p="md" colSpan={10}>
        {hasAnswers && (
          <>
            <TextWrapper size="sm">Placenta:</TextWrapper>
            <List fz="sm" withPadding>
              {placentaItems.map(
                (item, index) =>
                  (item.value || (item.showOther && item.otherValue)) && (
                    <List.Item key={`${item.label}-${item.value || item.otherValue}`}>
                      <Group gap="xs">
                        <TextWrapper size="sm" variant="bold">
                          {item.label}:
                        </TextWrapper>
                        <TextWrapper size="sm">{item.value}</TextWrapper>
                      </Group>
                    </List.Item>
                  ),
              )}
            </List>
          </>
        )}
        {hasNotes && (
          <TextWrapper pt="xs" size="sm">
            Placenta Comments: {notes}
          </TextWrapper>
        )}
        {showPreviousVersions && previousVersions ? (
          <PreviousVersions
            observation={observation}
            rowComponent={PlacentaRow}
            showPreviousVersions={showPreviousVersions}
          />
        ) : (
          <LastUpdated observation={observation} showPreviousVersions={false} />
        )}
      </Table.Td>
    </Table.Tr>
  );
}
